import { announceErpSessionChange } from "../utils/sessionChange";
import { isProtectedDemoViewer } from "@nimbus-erp/shared";
import {
  ApartmentOutlined,
  BellOutlined,
  CheckCircleOutlined,
  DownOutlined,
  KeyOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  QuestionCircleOutlined,
  SafetyCertificateOutlined,
  SearchOutlined,
  SettingOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { apiClient } from "@nimbus-erp/api-client";
import { PERMISSIONS, hasPermission } from "@nimbus-erp/shared";
import { BrandMark, StatusPill } from "@nimbus-erp/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { App, Button, Drawer, Dropdown, Tooltip } from "antd";
import type { ReactNode } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuthStore } from "../store/authStore";
import { WorkflowApprovalBridge } from "./WorkflowApprovalBridge";
import { canNavigate } from "../utils/navigationAccess";
import { DocumentTabs } from "./DocumentTabs";
import { CommandPalette } from "./CommandPalette";
import { ErpGlyph, type ErpGlyphName } from "./ErpGlyph";
import { ErpLoadingMark } from "./ErpLoading";
import { prefetchBusinessPage } from "../utils/prefetchBusinessPage";
import { inventoryViews } from "../utils/inventoryViews";
import {
  getPageDataOrigin,
  pageDataOriginKindLabels,
} from "../utils/pageDataOrigins";

interface ModuleItem {
  key: string;
  route: string;
  label: string;
  icon: ReactNode;
  permission?: string;
}

interface ContextItem {
  key: string;
  label: string;
  route: string;
  icon: ReactNode;
}

interface ContextSection {
  title: string;
  items: ContextItem[];
}

const moduleItems: ModuleItem[] = [
  {
    key: "workspace",
    route: "/workspace",
    label: "首页",
    icon: <ErpGlyph name="ledger" />,
  },
  {
    key: "sales",
    route: "/sales",
    label: "销售",
    icon: <ErpGlyph name="sales" />,
    permission: PERMISSIONS.salesRead,
  },
  {
    key: "purchase",
    route: "/purchase",
    label: "采购",
    icon: <ErpGlyph name="purchase" />,
    permission: PERMISSIONS.purchaseRead,
  },
  {
    key: "inventory",
    route: "/inventory",
    label: "库存",
    icon: <ErpGlyph name="inventory" />,
    permission: PERMISSIONS.inventoryRead,
  },
  {
    key: "manufacturing",
    route: "/production",
    label: "生产",
    icon: <ErpGlyph name="manufacturing" />,
    permission: PERMISSIONS.manufacturingRead,
  },
  {
    key: "planning",
    route: "/planning",
    label: "计划",
    icon: <ErpGlyph name="ledger" />,
    permission: PERMISSIONS.planningRead,
  },
  {
    key: "equipment",
    route: "/equipment",
    label: "设备",
    icon: <ErpGlyph name="manufacturing" />,
    permission: PERMISSIONS.equipmentRead,
  },
  {
    key: "quality",
    route: "/quality",
    label: "质量",
    icon: <SafetyCertificateOutlined />,
    permission: PERMISSIONS.qualityRead,
  },
  {
    key: "settlement",
    route: "/settlement",
    label: "往来",
    icon: <ErpGlyph name="settlement" />,
    permission: PERMISSIONS.settlementRead,
  },
  {
    key: "finance",
    route: "/finance",
    label: "财务",
    icon: <ErpGlyph name="finance" />,
    permission: PERMISSIONS.financeRead,
  },
  {
    key: "workflow",
    route: "/workflow",
    label: "审批",
    icon: <CheckCircleOutlined />,
    permission: PERMISSIONS.workflowRead,
  },
  {
    key: "analytics",
    route: "/analytics",
    label: "经营",
    icon: <ErpGlyph name="ledger" />,
    permission: PERMISSIONS.analyticsRead,
  },
  {
    key: "master-data",
    route: "/master-data",
    label: "基础资料",
    icon: <ErpGlyph name="master" />,
    permission: PERMISSIONS.masterDataRead,
  },
  {
    key: "organization",
    route: "/organization",
    label: "组织与员工",
    icon: <ErpGlyph name="organization" />,
  },
  {
    key: "system",
    route: "/subscription",
    label: "系统",
    icon: <ErpGlyph name="security" />,
  },
];

const pageTitles: Record<string, string> = {
  "/analytics": "经营分析与预警",
  "/tasks": "后台任务中心",
  "/workflow": "审批与待办",
  "/equipment": "设备管理",
  "/quality": "质量管理",
  "/planning": "计划与排产",
  "/production": "生产执行",
  "/manufacturing": "制造工程",
  "/workspace": "经营首页",
  "/sales": "销售业务",
  "/purchase": "采购业务",
  "/inventory": "库存业务",
  "/settlement": "往来资金",
  "/finance": "财务核算",
  "/master-data": "基础资料",
  "/organization": "组织与员工",
  "/subscription": "套餐与用量",
  "/customer-data-review": "上线资料核对",
  "/access": "权限与安全",
};
const referenceKindTitles: Record<string, string> = {
  TEAM: "班组",
  TAX_RATE: "税率",
  EXPENSE_ITEM: "费用项目",
  COST_ITEM: "成本项目",
  WAREHOUSE_LOCATION: "库位",
  VOUCHER_WORD: "凭证字",
};
const workspaceSectionTitles: Record<string, string> = {
  shortcuts: "业务入口",
  today: "今日待办",
  ledger: "经营账簿",
};

const masterContextItems: Array<[string, string, ErpGlyphName]> = [
  ["products", "商品档案", "master"],
  ["customers", "客户档案", "organization"],
  ["suppliers", "供应商档案", "purchase"],
  ["warehouses", "仓库档案", "inventory"],
  ["units", "计量单位", "ledger"],
  ["categories", "商品分类", "master"],
];

const contextForPath = (pathname: string): ContextSection[] => {
  if (pathname.startsWith("/analytics"))
    return [
      {
        title: "经营分析与预警",
        items: [
          ["overview", "经营总览"],
          ["alerts", "预警处理"],
          ["rules", "预警规则"],
        ].map(([key, label]) => ({
          key: key!,
          label: label!,
          route: `/analytics?view=${key}`,
          icon: <ErpGlyph name="ledger" />,
        })),
      },
    ];
  if (pathname.startsWith("/workflow"))
    return [
      {
        title: "审批中心",
        items: Object.entries({
          TODO: "我的待办",
          DONE: "我已处理",
          MINE: "我的申请",
          COPIED: "抄送给我",
          ALL: "全部申请",
          templates: "审批模板",
          managers: "直属主管",
        }).map(([key, label]) => ({
          key,
          label,
          route: `/workflow?view=${key}`,
          icon: <CheckCircleOutlined />,
        })),
      },
    ];
  if (pathname.startsWith("/equipment"))
    return [
      {
        title: "设备运行与维护",
        items: [
          ["tasks", "点检保养任务"],
          ["plans", "周期计划"],
          ["assets", "设备档案"],
          ["faults", "故障与维修"],
          ["analysis", "设备指标"],
        ].map(([key, label]) => ({
          key: key!,
          label: label!,
          route: `/equipment?view=${key}`,
          icon: <ErpGlyph name="manufacturing" />,
        })),
      },
    ];
  if (pathname.startsWith("/quality"))
    return [
      {
        title: "质量闭环",
        items: [
          ["inspections", "检验任务"],
          ["standards", "检验标准"],
          ["nonconformances", "不合格处置"],
          ["analysis", "质量分析"],
        ].map(([key, label]) => ({
          key: key!,
          label: label!,
          route: `/quality?view=${key}`,
          icon: <SafetyCertificateOutlined />,
        })),
      },
    ];
  if (pathname.startsWith("/planning"))
    return [
      {
        title: "需求与能力",
        items: [
          ["forecasts", "销售预测"],
          ["mps", "主生产计划 MPS"],
          ["mrp", "物料需求 MRP"],
          ["capacity", "能力排产"],
          ["policies", "计划参数"],
        ].map(([key, label]) => ({
          key: key!,
          label: label!,
          route: `/planning?view=${key}`,
          icon: <ErpGlyph name="ledger" />,
        })),
      },
    ];
  if (
    pathname.startsWith("/manufacturing") ||
    pathname.startsWith("/production")
  )
    return [
      {
        title: "制造工程",
        items: (
          [
            ["orders", "生产工单", "manufacturing"],
            ["boms", "BOM 版本", "master"],
            ["routings", "工艺路线", "organization"],
            ["centers", "工作中心", "inventory"],
            ["calendar", "工作日历", "ledger"],
          ] as Array<[string, string, ErpGlyphName]>
        ).map(([key, label, icon]) => ({
          key,
          label,
          route:
            key === "orders" ? "/production" : `/manufacturing?view=${key}`,
          icon: <ErpGlyph name={icon} />,
        })),
      },
    ];
  if (pathname.startsWith("/sales")) {
    return [
      {
        title: "销售业务",
        items: [
          {
            key: "orders",
            label: "销售订单",
            route: "/sales?view=orders",
            icon: <ErpGlyph name="sales" />,
          },
          {
            key: "executions",
            label: "销售出库",
            route: "/sales?view=executions",
            icon: <ErpGlyph name="outbound" />,
          },
          {
            key: "returns",
            label: "销售退货",
            route: "/sales?view=returns",
            icon: <ErpGlyph name="warning" />,
          },
        ],
      },
    ];
  }
  if (pathname.startsWith("/purchase")) {
    return [
      {
        title: "采购业务",
        items: [
          {
            key: "orders",
            label: "采购订单",
            route: "/purchase?view=orders",
            icon: <ErpGlyph name="purchase" />,
          },
          {
            key: "executions",
            label: "采购入库",
            route: "/purchase?view=executions",
            icon: <ErpGlyph name="inventory" />,
          },
          {
            key: "returns",
            label: "采购退货",
            route: "/purchase?view=returns",
            icon: <ErpGlyph name="warning" />,
          },
          {
            key: "expenses",
            label: "采购费用",
            route: "/purchase?view=expenses",
            icon: <ErpGlyph name="finance" />,
          },
        ],
      },
    ];
  }
  if (pathname.startsWith("/inventory")) {
    return [
      {
        title: "库存业务",
        items: inventoryViews.map(({ key, label }) => ({
          key,
          label,
          route: `/inventory?view=${key}`,
          icon: <ErpGlyph name={key === "logs" || key.endsWith("issues") || key.endsWith("outbounds") ? "outbound" : "inventory"} />,
        })),
      },
      {
        title: "资料",
        items: [
          {
            key: "warehouses",
            label: "仓库档案",
            route: "/master-data?view=warehouses",
            icon: <ErpGlyph name="master" />,
          },
        ],
      },
    ];
  }
  if (pathname.startsWith("/settlement")) {
    return [
      {
        title: "往来资金",
        items: [
          {
            key: "open-items",
            label: "往来余额",
            route: "/settlement?view=open-items",
            icon: <ErpGlyph name="settlement" />,
          },
          {
            key: "receipts",
            label: "收款单",
            route: "/settlement?view=receipts",
            icon: <ErpGlyph name="wallet" />,
          },
          {
            key: "payments",
            label: "付款单",
            route: "/settlement?view=payments",
            icon: <ErpGlyph name="outbound" />,
          },
          {
            key: "aging",
            label: "账龄分析",
            route: "/settlement?view=aging",
            icon: <ErpGlyph name="warning" />,
          },
          {
            key: "accounts",
            label: "结算账户",
            route: "/settlement?view=accounts",
            icon: <ErpGlyph name="wallet" />,
          },
          {
            key: "offsets",
            label: "退货对冲",
            route: "/settlement?view=offsets",
            icon: <ErpGlyph name="settlement" />,
          },
          {
            key: "applications",
            label: "预收预付核销",
            route: "/settlement?view=applications",
            icon: <ErpGlyph name="finance" />,
          },
          {
            key: "reconciliations",
            label: "往来对账单",
            route: "/settlement?view=reconciliations",
            icon: <ErpGlyph name="ledger" />,
          },
          {
            key: "invoices",
            label: "发票管理",
            route: "/settlement?view=invoices",
            icon: <ErpGlyph name="finance" />,
          },
        ],
      },
    ];
  }
  if (pathname.startsWith("/finance")) {
    return [
      {
        title: "财务核算",
        items: [
          {
            key: "vouchers",
            label: "记账凭证",
            route: "/finance?view=vouchers",
            icon: <ErpGlyph name="finance" />,
          },
          {
            key: "subjects",
            label: "会计科目",
            route: "/finance?view=subjects",
            icon: <ErpGlyph name="master" />,
          },
          {
            key: "trial",
            label: "试算平衡",
            route: "/finance?view=trial",
            icon: <ErpGlyph name="ledger" />,
          },
          {
            key: "auxiliary",
            label: "辅助核算账",
            route: "/finance?view=auxiliary",
            icon: <ErpGlyph name="ledger" />,
          },
          {
            key: "general",
            label: "总账",
            route: "/finance?view=general",
            icon: <ErpGlyph name="wallet" />,
          },
          {
            key: "periods",
            label: "会计期间",
            route: "/finance?view=periods",
            icon: <ErpGlyph name="subscription" />,
          },
          {
            key: "mappings",
            label: "业务科目映射",
            route: "/finance?view=mappings",
            icon: <ErpGlyph name="finance" />,
          },
          {
            key: "costs",
            label: "库存成本",
            route: "/finance?view=costs",
            icon: <ErpGlyph name="inventory" />,
          },
          {
            key: "statements",
            label: "财务报表",
            route: "/finance?view=statements",
            icon: <ErpGlyph name="finance" />,
          },
          {
            key: "opening",
            label: "期初余额",
            route: "/finance?view=opening",
            icon: <ErpGlyph name="finance" />,
          },
          {
            key: "closing",
            label: "期末结账",
            route: "/finance?view=closing",
            icon: <ErpGlyph name="subscription" />,
          },
          {
            key: "account-set",
            label: "账套信息",
            route: "/finance?view=account-set",
            icon: <ErpGlyph name="finance" />,
          },
        ],
      },
    ];
  }
  if (pathname.startsWith("/master-data")) {
    return [
      {
        title: "业务档案",
        items: masterContextItems.map(([key, label, icon]) => ({
          key,
          label,
          route: `/master-data?view=${key}`,
          icon: <ErpGlyph name={icon} />,
        })),
      },
      {
        title: "业务辅助资料",
        items: [
          ["TEAM", "班组"],
          ["TAX_RATE", "税率"],
          ["EXPENSE_ITEM", "费用项目"],
          ["COST_ITEM", "成本项目"],
          ["WAREHOUSE_LOCATION", "库位"],
          ["VOUCHER_WORD", "凭证字"],
        ].map(([key, label]) => ({
          key: `reference-${key}`,
          label: label!,
          route: `/master-data/references?kind=${key}`,
          icon: <ErpGlyph name="master" />,
        })),
      },
    ];
  }
  if (pathname.startsWith("/organization")) {
    return [
      {
        title: "组织管理",
        items: [
          {
            key: "employees",
            label: "员工与账号",
            route: "/organization?view=employees",
            icon: <ErpGlyph name="organization" />,
          },
          {
            key: "departments",
            label: "部门架构",
            route: "/organization?view=departments",
            icon: <ErpGlyph name="master" />,
          },
          {
            key: "positions",
            label: "岗位与在岗员工",
            route: "/organization/positions",
            icon: <ErpGlyph name="organization" />,
          },
        ],
      },
    ];
  }
  if (
    pathname.startsWith("/subscription") ||
    pathname.startsWith("/customer-data-review") ||
    pathname.startsWith("/access") ||
    pathname.startsWith("/tasks")
  ) {
    return [
      {
        title: "系统管理",
        items: [
          { key: "customer-data-review", label: "上线资料核对", route: "/customer-data-review", icon: <ErpGlyph name="ledger" /> },
          {
            key: "subscription",
            label: "套餐与用量",
            route: "/subscription",
            icon: <ErpGlyph name="subscription" />,
          },
          {
            key: "access",
            label: "权限与安全",
            route: "/access",
            icon: <ErpGlyph name="security" />,
          },
          {
            key: "tasks",
            label: "后台任务中心",
            route: "/tasks",
            icon: <ErpGlyph name="ledger" />,
          },
        ],
      },
    ];
  }
  return [
    {
      title: "经营工作台",
      items: [
        {
          key: "shortcuts",
          label: "业务入口",
          route: "/workspace#shortcuts",
          icon: <ErpGlyph name="master" />,
        },
        {
          key: "today",
          label: "今日待办",
          route: "/workspace#today",
          icon: <ErpGlyph name="warning" />,
        },
        {
          key: "ledger",
          label: "经营账簿",
          route: "/workspace#ledger",
          icon: <ErpGlyph name="wallet" />,
        },
      ],
    },
  ];
};

export function ErpShell() {
  const [contextCollapsed, setContextCollapsed] = useState(false);
  const [commandOpen, setCommandOpen] = useState(false);
  const [noticeOpen, setNoticeOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [pendingRoute, setPendingRoute] = useState<{
    url: string;
    label: string;
    moduleKey?: string;
    contextKey?: string;
  } | null>(null);
  const locationUrl = location.pathname + location.search + location.hash;
  const navigateWithFeedback = (
    route: string,
    label = "业务页面",
    moduleKey?: string,
    contextKey?: string,
  ) => {
    const target = new URL(route, window.location.origin);
    const url = target.pathname + target.search + target.hash;
    if (url !== locationUrl) {
      // Paint the selected state before a cold route chunk or a slow API can block it.
      flushSync(() => setPendingRoute({
        url,
        label,
        ...(moduleKey ? { moduleKey } : {}),
        ...(contextKey ? { contextKey } : {}),
      }));
    }
    // Commit the route before another rapid click can supersede it. This also
    // lets the visited-tab strip register every page the operator opened.
    navigate(route, { flushSync: true });
  };
  useEffect(() => {
    if (!pendingRoute) return;
    if (pendingRoute.url === locationUrl) {
      const frame = window.requestAnimationFrame(() => setPendingRoute(null));
      return () => window.cancelAnimationFrame(frame);
    }
    const timeout = window.setTimeout(() => setPendingRoute(null), 8000);
    return () => window.clearTimeout(timeout);
  }, [locationUrl, pendingRoute]);
  const queryClient = useQueryClient();
  const { user, reset } = useAuthStore();
  const { message } = App.useApp();
  const [demoActionNotice, setDemoActionNotice] = useState("");
  const isDemoViewer =
    isProtectedDemoViewer(user);
  useEffect(() => {
    apiClient.setDemoWriteProtected(isDemoViewer);
    document.documentElement.classList.toggle("erp-demo-readonly", isDemoViewer);
    const noticeTimers = new Set<number>();
    const explainProtectedWrite = (event: Event) => {
      const detail = (event as CustomEvent<{ message?: string }>).detail;
      const notice = detail?.message ?? "演示账号不能修改业务数据。";
      setDemoActionNotice(notice);
      const timer = window.setTimeout(() => {
        noticeTimers.delete(timer);
        const alreadyExplained = Array.from(
          document.querySelectorAll(".ant-message-notice"),
        ).some((item) => item.textContent?.includes(notice));
        if (!alreadyExplained) {
          message.warning({ key: "demo-write-protected", content: notice });
        }
      }, 120);
      noticeTimers.add(timer);
    };
    window.addEventListener("erp:demo-write-blocked", explainProtectedWrite);
    return () => {
      apiClient.setDemoWriteProtected(false);
      noticeTimers.forEach((timer) => window.clearTimeout(timer));
      document.documentElement.classList.remove("erp-demo-readonly");
      window.removeEventListener("erp:demo-write-blocked", explainProtectedWrite);
    };
  }, [isDemoViewer, message]);
  const workflowHome =
    user?.permissions.includes(PERMISSIONS.workflowApprove) ||
    user?.permissions.includes(PERMISSIONS.workflowManage)
      ? "/workflow?view=TODO"
      : "/workflow?view=COPIED";
  const moduleRoute = (item: ModuleItem) =>
    item.key === "workflow" ? workflowHome : item.route;
  const workspaceQuery = useQuery({
    queryKey: ["workspace"],
    queryFn: apiClient.workspace,
    staleTime: 30_000,
    enabled:
      !!user && hasPermission(user.permissions, PERMISSIONS.dashboardView),
  });

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setCommandOpen(true);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  useEffect(() => {
    if (!location.pathname.startsWith("/workspace") || !location.hash) return;
    const anchor = location.hash.slice(1);
    const frame = window.requestAnimationFrame(() => {
      document.getElementById(anchor)?.scrollIntoView({
        behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
          ? "auto"
          : "smooth",
        block: "start",
      });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [location.hash, location.pathname]);

  const reminders = useMemo(
    () =>
      workspaceQuery.data?.setupTasks.filter(
        (item) =>
          item.status !== "DONE" &&
          (!item.route || canNavigate(item.route, user?.permissions ?? [])),
      ) ?? [],
    [workspaceQuery.data, user],
  );
  const visibleModules = useMemo(
    () =>
      moduleItems.filter((item) =>
        canNavigate(item.route, user?.permissions ?? []),
      ),
    [user],
  );
  const contextSections = useMemo(
    () =>
      contextForPath(location.pathname)
        .map((section) => ({
          ...section,
          items: section.items.filter((item) =>
            canNavigate(item.route, user?.permissions ?? []),
          ),
        }))
        .filter((section) => section.items.length),
    [location.pathname, user],
  );
  const selectedContextItem = useMemo(() => {
    const currentView = new URLSearchParams(location.search).get("view");
    const currentKind = new URLSearchParams(location.search).get("kind");
    const fallbackHash = location.hash || "#shortcuts";
    const items = contextSections.flatMap((section) => section.items);
    const selected = items.find((item) => {
      const target = new URL(item.route, window.location.origin);
      if (target.pathname !== location.pathname) return false;
      if (target.hash) return target.hash === fallbackHash;
      if (target.searchParams.has("kind"))
        return target.searchParams.get("kind") === currentKind;
      if (target.searchParams.has("view"))
        return target.searchParams.get("view") === currentView;
      return true;
    });
    if (selected) return selected;
    if (!location.search && !location.hash) {
      if (location.pathname === "/workflow")
        return items.find((item) => item.route === workflowHome);
      return items.find(
        (item) =>
          new URL(item.route, window.location.origin).pathname ===
          location.pathname,
      );
    }
    return undefined;
  }, [contextSections, location.pathname, location.search, location.hash, workflowHome]);
  const selectedContextKey = pendingRoute?.contextKey ?? selectedContextItem?.key;
  const selectedContextLabel = selectedContextItem?.label;
  const activeModuleItem =
    visibleModules.find((item) => {
      if (item.key === "system") {
        return (
          location.pathname.startsWith("/customer-data-review") ||
          location.pathname.startsWith("/subscription") ||
          location.pathname.startsWith("/access") ||
          location.pathname.startsWith("/tasks")
        );
      }
      return item.key === "manufacturing"
        ? ["/manufacturing", "/production"].some((path) =>
            location.pathname.startsWith(path),
          )
        : location.pathname.startsWith(item.route);
    }) ?? visibleModules[0];
  const activeModule = pendingRoute?.moduleKey ?? activeModuleItem?.key ?? "workspace";
  const moduleNav = useRef<HTMLElement>(null);
  useEffect(() => {
    const nav = moduleNav.current,
      button = nav?.querySelector<HTMLButtonElement>(".is-active");
    if (nav && button) {
      const vertical = nav.scrollHeight > nav.clientHeight;
      if (vertical) {
        const top = button.offsetTop - nav.offsetTop;
        if (
          top < nav.scrollTop ||
          top + button.offsetHeight > nav.scrollTop + nav.clientHeight
        )
          nav.scrollTo({
            top: Math.max(
              0,
              top - nav.clientHeight / 2 + button.offsetHeight / 2,
            ),
          });
      } else {
        const left = button.offsetLeft - nav.offsetLeft;
        if (
          left < nav.scrollLeft ||
          left + button.offsetWidth > nav.scrollLeft + nav.clientWidth
        )
          nav.scrollTo({
            left: Math.max(
              0,
              left - nav.clientWidth / 2 + button.offsetWidth / 2,
            ),
          });
      }
    }
  }, [activeModule]);
  const actionableCount = reminders.filter(
    (item) => item.status === "READY",
  ).length;
  const pageTitle =
    selectedContextLabel ??
    (location.pathname.startsWith("/workspace")
      ? (workspaceSectionTitles[location.hash.slice(1) || "shortcuts"] ??
        "经营首页")
      : location.pathname.startsWith("/master-data/references")
        ? (referenceKindTitles[
            new URLSearchParams(location.search).get("kind") ?? "TEAM"
          ] ?? "参考资料")
        : undefined) ??
    Object.entries(pageTitles).find(([route]) =>
      location.pathname.startsWith(route),
    )?.[1] ??
    "企业工作台";
  const currentPageDataOrigin = useMemo(
    () => getPageDataOrigin(location.pathname, location.search),
    [location.pathname, location.search],
  );

  const navigateContext = (route: string) => {
    const item = contextSections.flatMap((section) => section.items)
      .find((candidate) => candidate.route === route);
    navigateWithFeedback(route, item?.label ?? "业务页面", activeModule, item?.key);
  };

  const logout = async () => {
    try {
      await apiClient.logout();
      announceErpSessionChange();
    } finally {
      reset();
      queryClient.clear();
      navigate("/login", { replace: true });
    }
  };

  const guidePermissions = user?.permissions ?? [];
  const canWriteBusiness = guidePermissions.some((permission) =>
    /_(MANAGE|AUDIT|APPROVE|RELEASE|POST|ACCEPT|CONCESSION|EXCEPTION)$/.test(permission),
  );
  const guideSteps = [
    {
      title: "先准备基础资料",
      description: "在基础资料里维护客户、供应商、计量单位、商品和仓库。订单里的下拉选项来自这里。",
      route: "/master-data",
      button: "打开基础资料",
    },
    {
      title: "录入订单并审核",
      description: "到销售或采购订单点击右上角“新建”。保存得到草稿，确认无误后再审核。",
      route: canNavigate("/sales", guidePermissions) ? "/sales" : "/purchase",
      button: "打开订单",
    },
    {
      title: "按已审核订单执行收发货",
      description: "在订单中选择出库或入库，填写仓库、数量与批次；完成后到库存查看余额和流水。",
      route: "/inventory",
      button: "查看库存",
    },
    {
      title: "核对往来和财务",
      description: "收发货会形成应收或应付；再登记收付款、核销与发票。发票登记不等于税务平台实际开票。",
      route: canNavigate("/settlement", guidePermissions) ? "/settlement" : "/finance",
      button: "打开往来财务",
    },
  ];

  return (
    <div className="erp-desktop erp-desktop--v4">
      <aside className="erp-module-bar erp-module-rail">
        <button
          type="button"
          className="erp-module-bar__brand"
          onClick={() => navigateWithFeedback("/workspace", "经营首页", "workspace")}
          aria-label="返回经营首页"
        >
          <BrandMark name="云猫 ERP" subtitle="BUSINESS OPERATIONS SUITE" />
        </button>

        <nav
          ref={moduleNav}
          className="erp-module-nav"
          aria-label="ERP 业务模块"
        >
          {visibleModules.map((item) => (
            <button
              type="button"
              key={item.key}
              className={activeModule === item.key ? "is-active" : ""}
              onPointerEnter={() => prefetchBusinessPage(moduleRoute(item))}
              onFocus={() => prefetchBusinessPage(moduleRoute(item))}
              onClick={() => navigateWithFeedback(moduleRoute(item), item.label, item.key)}
            >
              {item.icon}
              <span>{item.label}</span>
            </button>
          ))}
        </nav>

        <Dropdown
          trigger={["click"]}
          menu={{
            items: visibleModules.map((item) => ({
              key: item.key,
              label: item.label,
              icon: item.icon,
              onClick: () => navigateWithFeedback(moduleRoute(item), item.label, item.key),
            })),
            selectedKeys: [activeModule],
          }}
        >
          <button
            className="erp-module-more"
            aria-label="展开全部业务模块"
            type="button"
          >
            <DownOutlined />
          </button>
        </Dropdown>

        <span className="erp-module-rail__caption">业务模块</span>
      </aside>

      <section className="erp-shell-main">
        <header className="erp-command-bar">
          {pendingRoute && (
            <div className="erp-navigation-feedback" role="status" aria-live="polite">
              <ErpLoadingMark />
              正在打开{pendingRoute.label}
            </div>
          )}
          <div className="erp-command-context" aria-label="当前位置">
            <span>
              {workspaceQuery.data?.tenant.name ??
                user?.tenantName ??
                "制造企业"}
            </span>
            <i>·</i>
            <strong>
              {workspaceQuery.data?.tenant.packageName ?? "企业账套"}
            </strong>
            <DownOutlined />
          </div>

          <button
            className="erp-module-search"
            type="button"
            onClick={() => setCommandOpen(true)}
            aria-label="打开业务检索"
          >
            <span className="erp-module-search__mark">
              <SearchOutlined />
            </span>
            <span className="erp-module-search__copy">
              <small>指令中心</small>
              <strong>打开指令中心</strong>
            </span>
            <kbd>Ctrl K</kbd>
          </button>

          <div className="erp-module-tools">
            <span className="erp-online-state">
              <i /> 服务正常
            </span>
            <button
              type="button"
              className="erp-tool-action"
              aria-label="业务流程"
              onClick={() => setHelpOpen(true)}
            >
              <QuestionCircleOutlined />
              <span>业务流程</span>
            </button>
            <button
              type="button"
              className="erp-tool-action"
              aria-label="工作待办"
              onClick={() => setNoticeOpen(true)}
            >
              <BellOutlined />
              <span>工作待办</span>
              {actionableCount > 0 && <b>{actionableCount}</b>}
            </button>
            <button
              type="button"
              className="erp-tool-action"
              aria-label="系统设置"
              onClick={() => navigateWithFeedback("/access", "权限与安全", "system")}
            >
              <SettingOutlined />
              <span>系统设置</span>
            </button>
            <Dropdown
              trigger={["click"]}
              menu={{
                items: [
                  {
                    key: "account",
                    label: (
                      <span className="erp-account-menu__identity">
                        <strong>{user?.displayName ?? user?.username}</strong>
                        <small>{user?.tenantCode ?? "企业账号"}</small>
                      </span>
                    ),
                    icon: <UserOutlined />,
                    disabled: true,
                  },
                  { type: "divider" },
                  {
                    key: "security",
                    label: "权限与安全",
                    icon: <KeyOutlined />,
                    onClick: () => navigateWithFeedback("/access", "权限与安全", "system"),
                  },
                  {
                    key: "logout",
                    label: "退出登录",
                    icon: <LogoutOutlined />,
                    danger: true,
                    onClick: logout,
                  },
                ],
              }}
            >
              <button
                type="button"
                className="erp-account-trigger"
                aria-label={`账户与安全，${user?.displayName ?? user?.username ?? "当前用户"}`}
                title={user?.displayName ?? user?.username ?? "账户与安全"}
              >
                <span className="erp-account-trigger__mark">
                  <ApartmentOutlined />
                </span>
                <i className="erp-account-trigger__status" aria-hidden="true" />
              </button>
            </Dropdown>
          </div>
        </header>

        <DocumentTabs
          title={pageTitle}
          permissions={user?.permissions ?? []}
          onNavigate={(route, label) => navigateWithFeedback(route, label)}
        />

        <div
          className={`erp-workspace-frame ${contextCollapsed ? "is-context-collapsed" : ""}`}
        >
          <aside className="erp-context-panel">
            <div className="erp-context-panel__head">
              {!contextCollapsed && <strong>{pageTitle}</strong>}
              <Button
                type="text"
                size="small"
                icon={
                  contextCollapsed ? (
                    <MenuUnfoldOutlined />
                  ) : (
                    <MenuFoldOutlined />
                  )
                }
                onClick={() => setContextCollapsed((value) => !value)}
                aria-label={contextCollapsed ? "展开业务树" : "收起业务树"}
              />
            </div>
            <nav aria-label="当前模块业务树">
              {contextSections.map((section) => (
                <section key={section.title}>
                  {!contextCollapsed && <h2>{section.title}</h2>}
                  {section.items.map((item) => (
                    <Tooltip
                      key={item.key}
                      title={contextCollapsed ? item.label : undefined}
                      placement="right"
                    >
                      <button
                        type="button"
                        className={
                          selectedContextKey === item.key ? "is-active" : ""
                        }
                        onPointerEnter={() => prefetchBusinessPage(item.route)}
                        onFocus={() => prefetchBusinessPage(item.route)}
                        onClick={() => navigateContext(item.route)}
                      >
                        {item.icon}
                        {!contextCollapsed && <span>{item.label}</span>}
                      </button>
                    </Tooltip>
                  ))}
                </section>
              ))}
            </nav>
            <div className="erp-context-panel__tenant">
              <i />
              {!contextCollapsed && (
                <span>
                  <strong>{user?.tenantCode ?? "ENTERPRISE"}</strong>
                  <small>
                    {workspaceQuery.data?.tenant.packageName ?? "企业账套"}
                  </small>
                </span>
              )}
            </div>
          </aside>

          <main className="app-content" aria-busy={!!pendingRoute}>
            <Outlet />
            <WorkflowApprovalBridge />
          </main>
        </div>

        <footer className="erp-status-bar">
          <span>
            账套：
            <strong>{workspaceQuery.data?.tenant.name ?? "云猫 ERP"}</strong>
          </span>
          <span>
            操作员：<strong>{user?.displayName ?? user?.username}</strong>
          </span>
          <span>
            业务日期：<strong>{new Date().toLocaleDateString("zh-CN")}</strong>
          </span>
          <span>
            套餐：<strong>{workspaceQuery.data?.tenant.packageName ?? "加载中"}</strong>
          </span>
          {isDemoViewer && (
            <span
              className="erp-status-bar__demo"
              role="status"
              title={demoActionNotice || "演示账号可体验完整流程，提交不会写入业务数据"}
            >
              {demoActionNotice || "演示模式 · 可填写体验，提交不写入数据"}
            </span>
          )}
          <span className="erp-status-bar__right">
            <i /> 服务在线
          </span>
        </footer>
      </section>

      <CommandPalette
        open={commandOpen}
        onClose={() => setCommandOpen(false)}
      />

      <Drawer
        title="工作提醒"
        open={noticeOpen}
        onClose={() => setNoticeOpen(false)}
        size={390}
      >
        <div className="notice-summary">
          <CheckCircleOutlined />
          <div>
            <strong>
              {actionableCount > 0
                ? `有 ${actionableCount} 项工作可以继续`
                : "当前没有紧急待办"}
            </strong>
            <span>这里只统计当前可以实际处理的业务与配置</span>
          </div>
        </div>
        <div className="notice-list">
          {reminders.map((task) => (
            <button
              type="button"
              key={task.key}
              onClick={() => {
                if (task.route) navigate(task.route);
                setNoticeOpen(false);
              }}
              disabled={!task.route}
            >
              <span>
                <strong>{task.title}</strong>
                <small>{task.description}</small>
              </span>
              <StatusPill
                tone={task.status === "READY" ? "warning" : "neutral"}
              >
                {task.status === "READY" ? "待处理" : "规划中"}
              </StatusPill>
            </button>
          ))}
        </div>
      </Drawer>

      <Drawer
        title="业务流程与页面说明"
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        size={560}
      >
        <div className="erp-use-guide">
          <p>第一次使用，建议先走通一张销售或采购订单，再扩展到生产、计划和财务。</p>
          <section className="erp-use-guide__origin" aria-label="当前页面数据来源">
            <header>
              <span>当前页面 · {currentPageDataOrigin.title}</span>
              <b>{pageDataOriginKindLabels[currentPageDataOrigin.kind]}</b>
            </header>
            <dl>
              <div>
                <dt>数据从哪里来</dt>
                <dd>{currentPageDataOrigin.source}</dd>
              </div>
              <div>
                <dt>正确操作入口</dt>
                <dd>{currentPageDataOrigin.action}</dd>
              </div>
              <div>
                <dt>新增规则</dt>
                <dd>{currentPageDataOrigin.noCreateReason}</dd>
              </div>
            </dl>
          </section>
          {isDemoViewer ? (
            <div className="erp-use-guide__permission" role="status">
              当前为演示账号：可以打开新增、编辑、提交和审核界面并填写内容；保存、提交、审核等写入请求会在发送前拦截，演示数据不会被改变。
            </div>
          ) : !canWriteBusiness ? (
            <div className="erp-use-guide__permission" role="status">
              当前账号只有查看权限。新增、审核等按钮会隐藏；需要企业管理员在“权限与安全”中授权。
            </div>
          ) : null}
          <ol>
            {guideSteps.map((step) => (
              <li key={step.title}>
                <strong>{step.title}</strong>
                <span>{step.description}</span>
                {canNavigate(step.route, guidePermissions) && (
                  <Button size="small" onClick={() => {
                    setHelpOpen(false);
                    navigate(step.route);
                  }}>
                    {step.button}
                  </Button>
                )}
              </li>
            ))}
          </ol>
          <small>不同岗位看到的菜单和按钮不同。结果型台账没有“新增”是为了保留来源追溯；需要录入时，请按上方“正确操作入口”从来源单据开始。</small>
        </div>
        <div className="help-list">
          <div>
            <kbd>Ctrl K</kbd>
            <span>
              <strong>全局搜索</strong>
              <small>快速定位菜单和当前可用功能</small>
            </span>
          </div>
          <div>
            <ApartmentOutlined />
            <span>
              <strong>业务树</strong>
              <small>左侧菜单只展示当前模块内的单据与资料</small>
            </span>
          </div>
          <div>
            <SafetyCertificateOutlined />
            <span>
              <strong>权限检查</strong>
              <small>在权限与安全页面查看当前会话授权</small>
            </span>
          </div>
        </div>
      </Drawer>
    </div>
  );
}
